import { Box, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
    IRestaurantLocation,
    useCreateCompanyContext,
} from '../CreateCompanyContext'
import NextButton from './NextButton'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import checkSubDomainIsAvailable from 'Services/checkSubdomainAvailable'
import { useForm } from 'react-hook-form'
import AddressDetail, { TAddressDetailRef } from './AddressDetail'
import { useRef } from 'react'
import OWInput from 'components/OWForm/OWInput'
import WhiteTextField from './WhiteTextField'
import OWSwitch from 'components/OWForm/OWSwitch'
import convertDomainName from 'utilities/convertDomainName'
import getOverflowSectionSX from '../utils/getOverflowSectionSX'

let dupDomains: string[] = []

const validateScheme = yup.object({
    name: yup.string().required().min(5).max(61),
    subDomain: yup
        .string()
        .required()
        .min(5)
        .max(61)
        .matches(
            /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/,
            'subdomain must not have special character except hyphen.',
        )
        .test(
            'unavailable-subdomain',
            'This subdomain is unavailable.',
            (val) => {
                return !dupDomains.some(
                    (dup) =>
                        dup.trim().toLowerCase() ===
                        val?.trim().toLowerCase()!!,
                )
            },
        ),
})

function RestaurantLocation() {
    const { t } = useTranslation()
    const {
        onPrev,
        onSubmitRestaurantLocation,
        restaurantLocation,
    } = useCreateCompanyContext()
    const form = useForm<IRestaurantLocation>({
        defaultValues: restaurantLocation,
        resolver: yupResolver(validateScheme),
    })

    const addressRef = useRef<TAddressDetailRef>(null)

    const { control, watch, setValue } = form

    const onSubmit = async (data: IRestaurantLocation) => {
        try {
            const isSubDomainAvailable = await checkSubDomainIsAvailable(
                data.subDomain,
            )
            const ss = await addressRef.current?.submitForm()
            const newData = {
                ...data,
                address: ss!,
            }
            if (isSubDomainAvailable) {
                onSubmitRestaurantLocation(newData)
            } else {
                dupDomains = [...dupDomains, data.subDomain]
                form.setError('subDomain', {
                    message: 'This subdomain is unavailable.',
                })
            }
        } catch (error) {
            console.log('error error error: ', error)
        }
    }

    const locationAddressSameCompany = watch('locationAddressSameCompany')
    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={getOverflowSectionSX}>
                <Grid item xs={12}>
                    <OWInput
                        component={WhiteTextField}
                        color="info"
                        fullWidth
                        label={t('register.label.main_location_name') + ' *'}
                        autoComplete="off"
                        control={control}
                        name="name"
                        onChangeCallback={(e) => {
                            const name = e.target.value
                            const subdomain = convertDomainName(name)
                            setValue('subDomain', subdomain, {
                                shouldTouch: true,
                                shouldValidate: true,
                            })
                        }}
                    />
                </Grid>
                <Grid xs={12} item>
                    <OWInput
                        component={WhiteTextField}
                        color="info"
                        fullWidth
                        label={t('register.label.sub_domain') + ' *'}
                        control={control}
                        name="subDomain"
                        autoComplete="off"
                    />
                </Grid>
                <Grid xs={12} item>
                    <OWSwitch
                        control={control}
                        label={
                            <Typography color="#fff">
                                <b>{t('register.label.use_company_address')}</b>
                            </Typography>
                        }
                        name={'locationAddressSameCompany'}
                        color="info"
                    />
                </Grid>
                {!locationAddressSameCompany && (
                    <AddressDetail
                        defaultAddress={restaurantLocation.address}
                        ref={addressRef}
                    />
                )}
            </Grid>
            <Box textAlign={'center'} width={'100%'}>
                <NextButton text={'Prev'} isPrevious onClick={onPrev} />
                <NextButton
                    text={'Next'}
                    onClick={form.handleSubmit(onSubmit)}
                />
            </Box>
            <button type="submit" style={{ display: 'none' }}></button>
        </form>
    )
}

export default RestaurantLocation
